import { useCallback, useRef, useContext, useEffect, useState } from 'react';
import { useAfterInit } from './useAfterInit';
import Layers from '../view/Layer';
import Map from '../view/Map';
import { DrawExtent, FipsTracker, getTilesInView } from '../view/MeasureLayer';
import { OLMSLayer } from '../view/OLMSLayer';
import CensusLayer from '../view/CensusLayer';
import CountyLayer from '../view/CountyLayer';
import PoiLayer from '../view/PoiLayer';
import MapControls from '../view/MapControls';
import ZoomControl from '../view/ZoomControls';
import DownloadControl from '../view/DownloadControl';
import ZoomNumberControl from '../view/ZoomNumberControl';
import FullScreenControl from '../view/FullScreenControl';
import ZoomSelect from '../view/ZoomSelect';
import { DATA_URL, debugMode, mapStaticVariables, minCensusZoom } from '../model/StaticVariables';
import { StateLayer } from '../view/StateLayer';
import { RegionLayer } from '../view/RegionLayer';
import { HHSregionsLayer } from '../view/HHSregionsLayer';
import { GridDebugLayer } from '../view/GridDebugLayer';
import { FeatureOutlineLayer } from '../view/FeatureOutlineLayer';
import MapContext from '../controller/MapProvider';
import { InterfaceContext } from '../model/InterfaceProvider';
import { FeatureContext } from '../model/FeatureProvider';
import { MapMarkerLayer } from '../view/MapMarkerLayer';
import { HighlightRegionLayer } from '../view/HighlightRegionLayer';

const MapController = (props) => {
    // const { streetmap, year, setLoadStatus, marker, unsetSearch, tract, usState, scope, updateHoveredFeature, updatePinnedFeature, pinWindow, setIMap, search, setCensusEnabled } = useContext(InterfaceContext);
    const { streetmap, year, setLoadStatus, marker, unsetSearch, tract, usState, scope, updatePinnedFeature, setIMap, search, setCensusEnabled, downloadRegion } = useContext(InterfaceContext);
    const { updateHoveredFeature, setPinnedFeature, pinWindow } = useContext(FeatureContext);
    const { zoomRead: zoom, setZoom, map, setFips, setReferenceAdded, setTiles, setWait, setMap, boundingBox, setBbox } = useContext(MapContext);
    const defaultBbox = mapStaticVariables.extents.contiguous_us_and_pr;
    const [homeBbox, setHomeBbox] = useState(() => (scope === 'state' && usState?.bounds ? usState.bounds : defaultBbox));
    const localStateRef = useRef(false);
    const localTractRef = useRef(() => tract);
    const localScopeRef = useRef(() => scope);
    const streetmapLabels = `${DATA_URL}/style/light-gray-labels2.json`;
    const streetmapLinesShapes = `${DATA_URL}/style/blue_non_labels.json`;
    const featureRef = useRef(0);

    useEffect(() => (localStateRef.current = usState?.id || false), [usState]);
    useEffect(() => {
        localTractRef.current = tract;
        updateHoveredFeature(false);
        featureRef.current = 0;
    }, [tract]);
    useEffect(() => (localScopeRef.current = scope), [scope]);

    //=========================
    // Layer Filters
    const defaultLayerFilter = (lyr) => {
        if (lyr.get('opacity') === 0 || !lyr.get('visible')) return false; //ΔVΔVΔ: No invisible layers
        return lyr;
    };
    const sviLayerFilter = (lyr) => {
        const layerName = localTractRef.current == 'county' ? 'NationalCounty' : 'Census';
        if (lyr.get('title') !== layerName) return false;
        return lyr;
    };
    const poiLayerFilter = (lyr) => {
        if (lyr.get('title')?.indexOf('poi') === -1) return false; //ΔVΔVΔ: Must be poi layer or selected svi layer
        return lyr;
    };
    const compositeFilter = (lyr) => {
        if (!defaultLayerFilter(lyr)) return false;
        return sviLayerFilter(lyr) || poiLayerFilter(lyr);
    };
    const featureIsWithinScope = (ftr) => {
        var stFIPS = ftr.get('FIPS')?.substring(0, 2);
        var mustBeState = localScopeRef.current === 'state';
        if (!mustBeState) return true;
        var matchingState = localStateRef.current === stFIPS;
        return matchingState;
    };
    // Layer Filters
    //===============================
    const validSviFeature = useCallback(
        (ftr, mustBeClickable = false) => {
            if (!ftr) return false;
            if (!featureIsWithinScope(ftr)) return false;
            if (mustBeClickable && !ftr.get('sviValues')) return false;
            return true;
        },
        [usState]
    );

    const validPoiFeature = (ftr, mustBeClickable = false) => {
        if (!ftr) return false;
        if (!ftr.get('poi')) return false;
        if (mustBeClickable && ftr.get('count') > 10) return false;
        return true;
    };
    const validateFeature = (ftr, mustBeClickable = false) => {
        return validPoiFeature(ftr, mustBeClickable) || validSviFeature(ftr, mustBeClickable);
    };

    const handleCensusEnabledStatus = (map) => {
        const zoom = map.getView().getZoom();
        const censusShouldBeEnabled = zoom >= minCensusZoom;
        setCensusEnabled(censusShouldBeEnabled);
    };
    // ΔVΔVΔ: Only update feature when ID has changed
    const hoverFeatureHandler = useCallback((feature) => {
        const prevID = featureRef.current;
        const nextID = validateFeature(feature) ? feature.get('FIPS') : 0;
        const ftrObject = nextID === 0 ? false : feature;
        featureRef.current = nextID;
        if (prevID !== nextID) {
            updateHoveredFeature(ftrObject);
        }
    });
    const handleFeatureClick = useCallback((feature) => {
        var ftr = validateFeature(feature, true) ? feature : false;
        setPinnedFeature(ftr);
        pinWindow(Boolean(ftr));
    });
    useAfterInit(() => {
        setHomeBbox(boundingBox);
    }, [boundingBox]);

    useAfterInit(() => {
        setHomeBbox(search?.length === 4 ? search : mapStaticVariables.extents.contiguous_us_and_pr);
    }, [search]);

    return (
        <>
            <Map
                id={'mapContainer'}
                className={debugMode ? 'debugMode' : ''}
                extent={mapStaticVariables.extents.max_map_bounds}
                initViewExtent={homeBbox}
                center={mapStaticVariables.coordinates.usCenter4326}
                zoom={zoom}
                projection={mapStaticVariables.projections.mercator}
                onInitMap={(olmap) => {
                    setMap(olmap);
                    setIMap(olmap);
                }}
                onMove={(args) => {
                    handleCensusEnabledStatus(args.map);
                    const tiles = getTilesInView(args);
                    setTiles(tiles);
                }}
                layerFilter={compositeFilter}
                onHoverFeature={hoverFeatureHandler}
                onClickFeature={handleFeatureClick}
                onLoadStatus={(status) => {
                    setLoadStatus(status);
                }}
            >
                <Layers>
                    <OLMSLayer title="background" url={streetmapLinesShapes} visible={streetmap} />
                    <CensusLayer opacity={0.7} />
                    <CountyLayer opacity={0.7} />
                    <StateLayer zoom={zoom} downloadRegion={downloadRegion} />
                    <RegionLayer zoom={zoom} downloadRegion={downloadRegion} />
                    <HHSregionsLayer zoom={zoom} downloadRegion={downloadRegion} />
                    <HighlightRegionLayer zoom={zoom} downloadRegion={downloadRegion} />
                    <FeatureOutlineLayer />
                    <MapMarkerLayer markerCoords={[marker]} />
                    <OLMSLayer title="labels" url={streetmapLabels} />
                    <PoiLayer type="nursinghome" minZoom={6} />
                    <PoiLayer type="hospital" minZoom={6} />
                    <PoiLayer type="daycare" minZoom={6} />
                    <PoiLayer type="school" minZoom={6} />
                    {debugMode && (
                        <>
                            <GridDebugLayer />
                            {/* <DrawExtent extent={mapStaticVariables.extents.contiguous_us_and_pr} /> */}
                            <DrawExtent extent={[-19852126.727284186, 2144384.8703948213, -11359213.202471135, 6275282.0832699165]} />
                            {/* <DrawExtent extent={[-9855505.735686982, 6013681.267387853, -6622096.868618478, 4471260.408302792]} /> */}
                        </>
                    )}
                    <FipsTracker
                        year={year}
                        onChange={(fips) => {
                            setReferenceAdded(true);
                            setFips(fips);
                            setWait(false);
                        }}
                    />
                </Layers>
                <MapControls>
                    <DownloadControl />
                    <ZoomNumberControl zoom={zoom} onChange={setZoom} />
                    <ZoomControl />
                    <ZoomSelect />
                    <FullScreenControl onZoom={unsetSearch} initExtent={defaultBbox} />
                </MapControls>
            </Map>
        </>
    );
};
export default MapController;
